import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@getgo/chameleon-web-react-wrapper';
import classNames from 'classnames';
import Loading from "../Loading";
import React from 'react';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { useKeyDown } from "../../utils/hooks";
import { KeyCodes } from "../../utils/keys";
import ChipInput from '../ChipInput';
import classes from './index.module.scss';
const INITIAL_ITEM_COUNT = 2;
const DEBOUNCE_TIME = 300;
const MAXIMUM_VISIBLE_ITEMS = 10;
function getCount(items) {
    return items && items.length;
}
const ChipsDropdown = ({ className, placeholder, emptyMessage, hasMore, items, isSearchDebounced, getChipEntry, itemRenderer, cancelLabel, confirmLabel, isFocused, itemHeight, onRequestItems, onCancel, onConfirm, getId = item => item.id, onSelectionChange, itemCount: itemCountProps, hideButtons, }) => {
    const [debounceTime, setDebounceTime] = React.useState(DEBOUNCE_TIME);
    const [isLoading, setIsLoading] = React.useState(false);
    const [itemCount, setItemCount] = React.useState(INITIAL_ITEM_COUNT);
    const [height, setHeight] = React.useState((itemCountProps || INITIAL_ITEM_COUNT) * itemHeight);
    const [selectedItems, setSelectedItems] = React.useState([]);
    const [search, setSearch] = React.useState('');
    const [activePlaceholder, setActivePlaceholder] = React.useState(placeholder);
    const [isSearchEmpty, setIsSearchEmpty] = React.useState(items && items.length === 0);
    const reset = React.useCallback(() => {
        setActivePlaceholder(placeholder);
        setIsLoading(false);
        setSelectedItems([]);
    }, [placeholder]);
    const cancelCallback = React.useCallback(() => {
        reset();
        onCancel();
    }, [onCancel, reset]);
    React.useEffect(() => {
        onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(selectedItems);
    }, [selectedItems]);
    const isItemLoaded = (index) => (hasMore ? index < getCount(items) : true);
    const handleLoadMoreItems = () => {
        if (hasMore) {
            setIsLoading(true);
            onRequestItems(search);
        }
        return null;
    };
    const handleOnFocus = () => {
        if (!items) {
            setIsLoading(true);
            onRequestItems('');
        }
        else if (selectedItems.length > 0) {
            setActivePlaceholder('');
        }
    };
    const handleOnChange = (query) => {
        setSearch(query);
        setIsLoading(true);
        onRequestItems(query);
    };
    const handleOnItemClick = (item) => {
        setSelectedItems(selectedItems.find(currentItem => getId(currentItem) === getId(item)) ?
            selectedItems.filter(currentItem => getId(currentItem) !== getId(item)) :
            selectedItems.concat(item));
    };
    const handleOnDeleteItem = (chip) => {
        setSelectedItems(selectedItems.filter(selectedItem => getId(selectedItem) !== chip.id));
    };
    const handleOnConfirm = () => {
        if (selectedItems.length > 0) {
            reset();
            onConfirm(selectedItems);
        }
    };
    React.useEffect(() => {
        setDebounceTime(isSearchDebounced ? DEBOUNCE_TIME : 0);
    }, [isSearchDebounced]);
    React.useEffect(() => {
        setActivePlaceholder(placeholder);
    }, [placeholder]);
    // hasMore and infinite loading state handling
    React.useEffect(() => {
        const userCount = getCount(items);
        setIsLoading(!items);
        setIsSearchEmpty(items && !userCount);
        setItemCount(hasMore ? userCount + 1 : items ? Math.max(userCount, 1) : 0);
        if (!itemCountProps) {
            const maxHeight = Math.floor((window.innerHeight * 0.75) / itemHeight) * itemHeight;
            setHeight(Math.min(userCount ?
                Math.min(userCount, MAXIMUM_VISIBLE_ITEMS) * itemHeight :
                INITIAL_ITEM_COUNT * itemHeight, maxHeight));
        }
    }, [items, hasMore, itemCountProps]);
    useKeyDown(() => {
        document.activeElement.blur();
        cancelCallback();
    }, [KeyCodes.ESCAPE]);
    const ItemRenderer = ({ index, style }) => {
        const selected = items && items[index] &&
            selectedItems.find(selectedItem => getId(selectedItem) === getId(items[index]));
        return index === 0 && isSearchEmpty && !isLoading ?
            (_jsx("div", Object.assign({ className: classes.contentItemEmpty, style: style }, { children: emptyMessage }), void 0)) :
            itemRenderer({
                style,
                className: selected && classes.itemAdded,
                selected: !!selected,
                key: index,
                item: items && items[index],
                onClick: handleOnItemClick,
            });
    };
    return (_jsxs("div", Object.assign({ className: classNames(classes.dropdown, className) }, { children: [_jsx(ChipInput, { chips: selectedItems, getChipEntry: getChipEntry, keepFocus: true, isFocused: isFocused, debounce: debounceTime, placeholder: activePlaceholder, onDeleteChip: handleOnDeleteItem, onFocus: handleOnFocus, onChange: handleOnChange }, void 0), _jsxs("div", Object.assign({ className: classes.dropdownList }, { children: [_jsxs("div", Object.assign({ className: classes.content }, { children: [_jsx(InfiniteLoader, Object.assign({ isItemLoaded: isItemLoaded, loadMoreItems: handleLoadMoreItems, itemCount: itemCount }, { children: ({ onItemsRendered, ref }) => (_jsx(FixedSizeList, Object.assign({ ref: ref, onItemsRendered: onItemsRendered, itemCount: itemCount, itemSize: itemHeight, height: height, width: '100%' }, { children: ItemRenderer }), void 0)) }), void 0), isLoading && (_jsx(Loading, {}, void 0))] }), void 0), !hideButtons && (_jsxs("footer", Object.assign({ className: classes.listActions }, { children: [_jsx(Button, Object.assign({ variant: "secondary", onClick: cancelCallback }, { children: cancelLabel }), void 0), _jsx(Button, Object.assign({ disabled: selectedItems.length === 0, onClick: handleOnConfirm }, { children: confirmLabel }), void 0)] }), void 0))] }), void 0)] }), void 0));
};
export default ChipsDropdown;
//# sourceMappingURL=index.js.map